/**
 * Debounce utility function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(func, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;
  /**
   * @param {any[]} args
   */
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * BrandsListPagination - Client-side pagination for brands list
 * Handles showing/hiding cards, search, sorting, and updating pagination controls
 */
class BrandsListPagination {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.container = element;
    this.perPage = 24;
    this.currentPage = this.getPageFromURL();
    this.totalPages = 0;
    /** @type {HTMLElement[]} */
    this.allCards = [];
    /** @type {HTMLElement[]} */
    this.allCardsOriginal = []; // Store original order
    /** @type {HTMLElement[]} */
    this.filteredCards = []; // Currently visible after search/sort
    this.searchQuery = "";
    this.currentSort = "default";

    // Create debounced search handler (300ms delay)
    this.searchDebounced = debounce((/** @type {string} */ query) => {
      this.handleSearch(query);
    }, 300);

    // Get translations from data attributes
    this.translations = {
      noResultsTitle:
        this.container.dataset.noResultsTitle || "No Results Found",
      noResultsText:
        this.container.dataset.noResultsText ||
        "We couldn't find any brands matching your search.",
      paginationPrev: this.container.dataset.paginationPrev || "Previous page",
      paginationNext: this.container.dataset.paginationNext || "Next page",
      paginationPage:
        this.container.dataset.paginationPage || "Page {{ page }}",
    };

    this.init();
  }

  init() {
    // Get all brand cards
    this.allCards = Array.from(
      this.container.querySelectorAll(".brands-list__item"),
    );
    this.allCardsOriginal = [...this.allCards]; // Clone original order
    this.filteredCards = [...this.allCards];

    if (this.allCards.length === 0) return;

    // Calculate total pages
    this.totalPages = Math.ceil(this.allCards.length / this.perPage);

    // Ensure current page is within bounds
    if (this.currentPage < 1) this.currentPage = 1;
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;

    // Show initial page
    this.showPage(this.currentPage);

    // Create and render pagination controls
    this.renderPaginationControls();

    // Bind events
    this.bindEvents();
  }

  getPageFromURL() {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get("page") || "1", 10);
    return isNaN(page) ? 1 : page;
  }

  /**
   * @param {number} page
   */
  showPage(page) {
    const startIdx = (page - 1) * this.perPage;
    const endIdx = startIdx + this.perPage;

    // Hide all cards first
    this.filteredCards.forEach((card, idx) => {
      if (idx >= startIdx && idx < endIdx) {
        card.style.display = "";
      } else {
        card.style.display = "none";
      }
    });

    // Update current page
    this.currentPage = page;

    // Scroll to top of section
    this.scrollToTop();
  }

  scrollToTop() {
    const sectionTop =
      this.container.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({
      top: sectionTop,
      behavior: "smooth",
    });
  }

  /**
   * @param {number} page
   */
  updateURL(page) {
    const url = new URL(window.location.href);
    const pageNum = Number(page);

    if (pageNum === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", pageNum.toString());
    }

    window.history.pushState({}, "", url.toString());
  }

  /**
   * Handle search input
   * @param {string} query
   */
  handleSearch(query) {
    this.searchQuery = query.toLowerCase().trim();
    this.applyFiltersAndSort();
  }

  /**
   * Handle sort selection
   * @param {string} sortType
   */
  handleSort(sortType) {
    this.currentSort = sortType;
    this.applyFiltersAndSort();

    // Update active state on sort options
    document.querySelectorAll(".brands-list__sort-option").forEach((btn) => {
      const htmlBtn = /** @type {HTMLElement} */ (btn);
      htmlBtn.classList.toggle("active", htmlBtn.dataset.sort === sortType);
    });
  }

  /**
   * Apply search filter and sorting
   */
  applyFiltersAndSort() {
    // 1. Start with all cards in original order
    let cards = [...this.allCardsOriginal];

    // 2. Apply search filter
    if (this.searchQuery) {
      cards = cards.filter((card) => {
        const htmlCard = /** @type {HTMLElement} */ (card);
        const brandName = (htmlCard.dataset.brandName || "").toLowerCase();
        return brandName.includes(this.searchQuery);
      });
    }

    // 3. Apply sorting
    if (this.currentSort === "az") {
      cards.sort((a, b) => {
        const htmlA = /** @type {HTMLElement} */ (a);
        const htmlB = /** @type {HTMLElement} */ (b);
        const nameA = (htmlA.dataset.brandName || "").toLowerCase();
        const nameB = (htmlB.dataset.brandName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (this.currentSort === "za") {
      cards.sort((a, b) => {
        const htmlA = /** @type {HTMLElement} */ (a);
        const htmlB = /** @type {HTMLElement} */ (b);
        const nameA = (htmlA.dataset.brandName || "").toLowerCase();
        const nameB = (htmlB.dataset.brandName || "").toLowerCase();
        return nameB.localeCompare(nameA);
      });
    }
    // 'default' keeps original order

    // 4. Update filtered cards
    this.filteredCards = cards;
    this.allCards = cards;

    // 5. Recalculate pagination
    this.totalPages = Math.ceil(cards.length / this.perPage);
    this.currentPage = 1;

    // 6. Re-render
    this.updateDOM();
    this.updateBrandCount();
  }

  /**
   * Update DOM with filtered/sorted cards
   */
  updateDOM() {
    const container = this.container.querySelector(".resource-list");
    if (!container) return;

    // Clear existing cards
    container.innerHTML = "";

    // Check if there are no results
    if (this.filteredCards.length === 0) {
      // Show "No Results" message
      const noResults = document.createElement("div");
      noResults.className = "brands-list__no-results";
      noResults.innerHTML = `
        <div class="brands-list__no-results-content">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
            <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M11 8v6M8 11h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <h3>${this.translations.noResultsTitle}</h3>
          <p>${this.translations.noResultsText}</p>
        </div>
      `;
      container.appendChild(noResults);
    } else {
      // Add filtered/sorted cards
      this.filteredCards.forEach((card) => {
        container.appendChild(card);
      });
    }

    // Show current page
    this.showPage(this.currentPage);

    // Update pagination controls
    this.updatePaginationControls();
  }

  /**
   * Update brand count display
   */
  updateBrandCount() {
    const countEl = document.querySelector(".brands-list__count");
    if (countEl) {
      // Note: We're not using t filter here because pluralization in JS is tricky
      // But the "Showing" part is already in Liquid for initial load.
      // For simplicity, we'll keep the logic but we could also pass the whole string template.
      // However, the user asked to remove hardcoded text.
      // Let's use the count meta from liquid if possible or just update the number.
      const count = this.filteredCards.length;
      const regex = new RegExp(/\d+/);
      const currentText = countEl.textContent || "";
      countEl.textContent = currentText.replace(regex, count.toString());
    }
  }

  renderPaginationControls() {
    // Only show pagination if more than 1 page
    if (this.totalPages <= 1) {
      // Remove existing pagination if it exists
      const existing = this.container.querySelector(".brands-list__pagination");
      if (existing) existing.remove();
      return;
    }

    const paginationWrapper = document.createElement("nav");
    paginationWrapper.className = "brands-list__pagination pagination";
    paginationWrapper.setAttribute("aria-label", "Pagination");

    const list = document.createElement("ul");
    list.className = "pagination__list";
    list.setAttribute("role", "list");

    // Previous button
    list.appendChild(this.createPrevButton());

    // Page numbers
    const pages = this.getPageNumbers();
    pages.forEach((pageNum) => {
      if (pageNum === "...") {
        list.appendChild(this.createEllipsis());
      } else {
        list.appendChild(
          this.createPageButton(/** @type {number} */ (pageNum)),
        );
      }
    });

    // Next button
    list.appendChild(this.createNextButton());

    paginationWrapper.appendChild(list);

    // Insert pagination after the resource list
    const resourceList = this.container.querySelector(".resource-list");
    if (resourceList && resourceList.parentNode) {
      resourceList.parentNode.insertBefore(
        paginationWrapper,
        resourceList.nextSibling,
      );
    }

    this.paginationElement = paginationWrapper;
  }

  getPageNumbers() {
    const pages = [];
    const maxVisible = 7; // Maximum page numbers to show

    if (this.totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Determine range
      let start = Math.max(2, this.currentPage - 1);
      let end = Math.min(this.totalPages - 1, this.currentPage + 1);

      // If we are at the beginning (page 1, 2, or 3), show 1, 2, 3
      if (this.currentPage <= 3) {
        end = Math.max(3, end);
      }

      // If we are at the end, show more context there
      if (this.currentPage >= this.totalPages - 2) {
        start = Math.min(start, this.totalPages - 2);
      }

      if (start > 2) {
        pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages - 1) {
        pages.push("...");
      }

      if (this.totalPages > 1) {
        pages.push(this.totalPages);
      }
    }

    return pages;
  }

  createPrevButton() {
    const li = document.createElement("li");
    li.className = "pagination__item";

    const hasPrev = this.currentPage > 1;

    if (hasPrev) {
      const link = document.createElement("a");
      link.href = `?page=${this.currentPage - 1}`;
      link.className = "pagination__link pagination__link--arrow";
      link.setAttribute("aria-label", this.translations.paginationPrev);
      link.innerHTML =
        '<svg style="transform: rotate(180deg);" xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 13L7 7L1 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      link.addEventListener("click", (e) =>
        this.handlePageClick(e, this.currentPage - 1),
      );
      li.appendChild(link);
    } else {
      const span = document.createElement("span");
      span.className =
        "pagination__link pagination__link--arrow pagination__link--disabled";
      span.setAttribute("aria-label", this.translations.paginationPrev);
      span.setAttribute("aria-disabled", "true");
      span.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M7 13L1 7L7 1" stroke="#A3A3A3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      li.appendChild(span);
    }

    return li;
  }

  createNextButton() {
    const li = document.createElement("li");
    li.className = "pagination__item";

    const hasNext = this.currentPage < this.totalPages;

    if (hasNext) {
      const link = document.createElement("a");
      link.href = `?page=${this.currentPage + 1}`;
      link.className = "pagination__link pagination__link--arrow";
      link.setAttribute("aria-label", this.translations.paginationNext);
      link.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 13L7 7L1 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      link.addEventListener("click", (e) =>
        this.handlePageClick(e, this.currentPage + 1),
      );
      li.appendChild(link);
    } else {
      const span = document.createElement("span");
      span.className =
        "pagination__link pagination__link--arrow pagination__link--disabled";
      span.setAttribute("aria-label", this.translations.paginationNext);
      span.setAttribute("aria-disabled", "true");
      span.innerHTML =
        '<svg style="transform: rotate(180deg);" xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M7 13L1 7L7 1" stroke="#A3A3A3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      li.appendChild(span);
    }

    return li;
  }

  /**
   * @param {number} pageNum
   */
  createPageButton(pageNum) {
    const li = document.createElement("li");
    li.className = "pagination__item";

    const isCurrent = pageNum === this.currentPage;

    if (isCurrent) {
      const span = document.createElement("span");
      span.className =
        "pagination__link pagination__link--page pagination__link--current";
      span.setAttribute("aria-current", "page");
      span.textContent = String(pageNum);
      li.appendChild(span);
    } else {
      const link = document.createElement("a");
      link.href = `?page=${pageNum}`;
      link.className = "pagination__link pagination__link--page";
      const pageAriatLabel = this.translations.paginationPage.replace(
        "{{ page }}",
        pageNum.toString(),
      );
      link.setAttribute("aria-label", pageAriatLabel);
      link.textContent = String(pageNum);
      link.addEventListener("click", (e) => this.handlePageClick(e, pageNum));
      li.appendChild(link);
    }

    return li;
  }

  createEllipsis() {
    const li = document.createElement("li");
    li.className = "pagination__item pagination__item--gap";

    const span = document.createElement("span");
    span.className = "pagination__link pagination__link--gap";
    span.setAttribute("aria-hidden", "true");
    span.innerHTML = "&hellip;";

    li.appendChild(span);
    return li;
  }

  /**
   * @param {Event} e
   * @param {number} page
   */
  handlePageClick(e, page) {
    e.preventDefault();

    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }

    // Update URL
    this.updateURL(page);

    // Show new page
    this.showPage(page);

    // Update pagination controls
    this.updatePaginationControls();
  }

  updatePaginationControls() {
    if (this.paginationElement) {
      this.paginationElement.remove();
    }

    // Render new pagination
    this.renderPaginationControls();
  }

  bindEvents() {
    // Handle browser back/forward buttons
    window.addEventListener("popstate", () => {
      this.currentPage = this.getPageFromURL();
      this.showPage(this.currentPage);
      this.updatePaginationControls();
    });

    // Search input - use debounced handler
    const searchInput = document.getElementById("brands-search");
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        const target = /** @type {HTMLInputElement} */ (e.target);
        this.searchDebounced(target.value);
      });
    }

    // Sort button toggle dropdown
    const sortBtn = document.getElementById("sort-btn");
    const sortDropdown = document.getElementById("sort-dropdown");

    if (sortBtn && sortDropdown) {
      sortBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        sortDropdown.classList.toggle("active");
      });

      // Sort options
      sortDropdown.querySelectorAll("[data-sort]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const target = /** @type {HTMLElement} */ (e.currentTarget);
          const sortType = target.dataset.sort;
          if (sortType) {
            this.handleSort(sortType);
          }
          sortDropdown.classList.remove("active");
        });
      });

      // Close dropdown on outside click
      document.addEventListener("click", (e) => {
        const target = /** @type {Node} */ (e.target);
        if (!sortBtn.contains(target) && !sortDropdown.contains(target)) {
          sortDropdown.classList.remove("active");
        }
      });
    }
  }
}

// Initialize pagination when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const brandsListSection = /** @type {HTMLElement | null} */ (
    document.querySelector(".section-resource-list:has(.brands-list__item)")
  );
  if (brandsListSection) {
    new BrandsListPagination(brandsListSection);
  }
});
